import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { formatCurrency, calculateUnitCost } from "@/lib/currencyUtils";
import { Plus, X, Check, ChevronsUpDown, Leaf, Wheat, Flame, MilkOff, Droplet, Languages, Sparkles, ChefHat } from "lucide-react";
import { cn } from "@/lib/utils";
import { HelpTip } from "@/components/ui/help-tip";
import { CreatableCombobox } from "@/components/ui/creatable-combobox";
import { SUPPORTED_LANGUAGES } from "@/components/GlobalLanguageSwitcher";

const DEFAULT_RECIPE_CATEGORIES = [
  "Starters",
  "Soups & Salads",
  "Mains",
  "Sides",
  "Desserts",
  "Beverages",
  "Specials",
  "Other",
];

// Dietary tag set surfaced on the public Guest Menu.
const DIETARY_TAGS = [
  { value: "Veg", label: "Veg", icon: Leaf },
  { value: "Vegan", label: "Vegan", icon: Leaf },
  { value: "Gluten-Free", label: "GF", icon: Wheat },
  { value: "Dairy-Free", label: "Dairy-Free", icon: MilkOff },
  { value: "Nut-Free", label: "Nut-Free", icon: Droplet },
  { value: "Halal", label: "Halal", icon: Leaf },
  { value: "Kosher", label: "Kosher", icon: Leaf },
];

// Canonical yield-unit list — mirrors the backend whitelist in deps.py
// (YIELD_UNIT_OPTIONS).  Keep these in sync.
const YIELD_UNIT_GROUPS = [
  { label: "Volume", options: ["ml", "L", "fl oz", "gal", "cup", "pt", "qt"] },
  { label: "Weight", options: ["g", "kg", "oz", "lb"] },
  { label: "Count",  options: ["each", "piece", "slice", "portion"] },
];

const DEPARTMENTS = [
  { value: "Kitchen", label: "Kitchen", emoji: "👨‍🍳" },
  { value: "Bar", label: "Bar", emoji: "🍸" },
  { value: "Lab", label: "Lab (Brewery/Fermentation)", emoji: "🧪" },
  { value: "Bakery", label: "Bakery", emoji: "🥖" },
  { value: "Pastry", label: "Pastry/Desserts", emoji: "🍰" }
];

// Demo translations for the Tourist Menu upgrade preview.
// A curated "wow" dish rendered in 5 tourist-heavy languages — not
// user-specific, but illustrates the value of Gemini auto-translation.
const UPGRADE_PREVIEW_DEMOS = {
  fr: {
    name: "Magret de canard laqué au miel",
    notes: "Magret rôti, glaçage miel-soja, garniture saisonnière.",
  },
  es: {
    name: "Pechuga de pato glaseada con miel",
    notes: "Pechuga asada, glaseado de miel y soja, guarnición de temporada.",
  },
  ja: {
    name: "蜂蜜グレーズ鴨の胸肉",
    notes: "ロースト鴨胸肉、蜂蜜醤油グレーズ、季節の付け合わせ。",
  },
  zh: {
    name: "蜂蜜釉汁鸭胸",
    notes: "慢烤鸭胸，蜂蜜酱油釉汁，时令配菜。",
  },
  ar: {
    name: "صدر البط المزجج بالعسل",
    notes: "صدر بط مشوي، تزجيج العسل والصويا، طبق جانبي موسمي.",
  },
};
const UPGRADE_PREVIEW_ORIGINAL = {
  name: "Honey-Glazed Duck Breast",
  notes: "Slow-roasted duck breast, honey-soy glaze, seasonal side.",
};

const RecipeModal = ({ isOpen, onClose, recipe, currency, targetMargin }) => {
  const { t } = useTranslation();
  const [translations, setTranslations] = useState({});
  const [translating, setTranslating] = useState(false);
  const [translationUsage, setTranslationUsage] = useState(null);

  // BOH (Sarvam) — kitchen prep translations into Indic languages.
  // INDIC_LANGUAGES live in GlobalLanguageSwitcher; we re-declare a thin
  // local copy here to avoid a circular import (the switcher imports from
  // i18n which imports from RecipeModal in some build configs).
  const BOH_LANGUAGES = [
    { code: "hi-IN", label: "हिन्दी",   english: "Hindi" },
    { code: "ta-IN", label: "தமிழ்",     english: "Tamil" },
    { code: "te-IN", label: "తెలుగు",    english: "Telugu" },
    { code: "mr-IN", label: "मराठी",     english: "Marathi" },
    { code: "bn-IN", label: "বাংলা",     english: "Bengali" },
    { code: "gu-IN", label: "ગુજરાતી",   english: "Gujarati" },
    { code: "kn-IN", label: "ಕನ್ನಡ",     english: "Kannada" },
    { code: "ml-IN", label: "മലയാളം",   english: "Malayalam" },
    { code: "pa-IN", label: "ਪੰਜਾਬੀ",    english: "Punjabi" },
    { code: "od-IN", label: "ଓଡ଼ିଆ",       english: "Odia" },
  ];
  const [bohTranslations, setBohTranslations] = useState({});
  const [bohActiveLang, setBohActiveLang] = useState("hi-IN");
  const [translatingBoh, setTranslatingBoh] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    category: "Mains",
    department: "Kitchen",
    serving_size: "1",
    ingredients: [],
    menu_price: "",
    is_active: false,
    notes: "",
    other_category_name: "",
    calories: "",
    dietary_tags: [],
    spice_level: "",
    yield_amount: "",
    yield_unit: "",
    portion_size: "",
  });
  const [availableIngredients, setAvailableIngredients] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [selectedIngredientId, setSelectedIngredientId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [calculatedCost, setCalculatedCost] = useState("0.00");
  const [calculatedProfit, setCalculatedProfit] = useState("0.00");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [categoryOptions, setCategoryOptions] = useState(DEFAULT_RECIPE_CATEGORIES);
  // Inline Tourist-Menu upgrade preview (shown when a free-tier chef clicks
  // the paywall CTA — converts rejection into a guided Pro upgrade).
  const [showUpgradePreview, setShowUpgradePreview] = useState(false);
  const [previewLocale, setPreviewLocale] = useState("fr");

  useEffect(() => {
    if (isOpen) {
      fetchIngredients();
      // Load merged recipe categories (defaults + per-user custom)
      axios
        .get(`${API}/recipe-categories`)
        .then((res) => {
          const all = res.data?.all;
          if (Array.isArray(all) && all.length > 0) setCategoryOptions(all);
        })
        .catch(() => {
          /* silently fall back to defaults */
        });

      if (recipe) {
        // Edit mode
        setFormData({
          name: recipe.name,
          category: recipe.category,
          serving_size: recipe.serving_size,
          ingredients: recipe.ingredients || [],
          menu_price: recipe.menu_price,
          is_active: recipe.is_active,
          notes: recipe.notes || "",
          other_category_name: "",
          calories: recipe.calories ?? "",
          dietary_tags: Array.isArray(recipe.dietary_tags) ? recipe.dietary_tags : [],
          spice_level: recipe.spice_level ?? "",
          yield_amount: recipe.yield_amount || "",
          yield_unit: recipe.yield_unit || "",
          portion_size: recipe.portion_size || "",
        });
        setTranslations(recipe.translations || {});
        setBohTranslations(recipe.translated_methods || {});
        // Pull current AI usage for footer display
        axios
          .get(`${API}/usage/ai-translations`)
          .then((res) => setTranslationUsage(res.data))
          .catch(() => setTranslationUsage(null));
      } else {
        // Add mode - reset form
        setFormData({
          name: "",
          category: "Mains",
          serving_size: "1",
          ingredients: [],
          menu_price: "",
          is_active: false,
          notes: "",
          other_category_name: "",
          calories: "",
          dietary_tags: [],
          spice_level: "",
          yield_amount: "",
          yield_unit: "",
          portion_size: "",
        });
      }
      setErrors({});
    }
  }, [recipe, isOpen]);

  const handleCreateRecipeCategory = async (name) => {
    try {
      await axios.post(`${API}/recipe-categories`, { name });
      setCategoryOptions((prev) =>
        prev.some((c) => c.toLowerCase() === name.toLowerCase()) ? prev : [...prev, name]
      );
      toast.success(`Menu section “${name}” added`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't create category";
      toast.error(msg);
      throw err;
    }
  };

  // Calculate cost and profit whenever ingredients or menu price changes
  useEffect(() => {
    calculateTotals();
  }, [formData.ingredients, formData.menu_price, availableIngredients]);

  const fetchIngredients = async () => {
    try {
      const response = await axios.get(`${API}/ingredients`);
      setAvailableIngredients(response.data);
    } catch (error) {
      console.error("Error fetching ingredients:", error);
    }
  };

  const calculateTotals = () => {
    let totalCost = 0;

    formData.ingredients.forEach(recipeIng => {
      const ingredient = availableIngredients.find(i => i.id === recipeIng.ingredient_id);
      if (ingredient) {
        const unitCost = parseFloat(ingredient.unit_cost);
        const qty = parseFloat(recipeIng.quantity);
        totalCost += unitCost * qty;
      }
    });

    const costStr = totalCost.toFixed(2);
    setCalculatedCost(costStr);

    // Calculate PlateProfit %
    if (formData.menu_price && parseFloat(formData.menu_price) > 0) {
      const price = parseFloat(formData.menu_price);
      const profit = ((price - totalCost) / price) * 100;
      setCalculatedProfit(profit.toFixed(2));
    } else {
      setCalculatedProfit("0.00");
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleAddIngredient = () => {
    if (!selectedIngredientId || !quantity || parseFloat(quantity) <= 0) {
      toast.error("Please select an ingredient and enter a valid quantity");
      return;
    }

    // Check if ingredient already added
    if (formData.ingredients.some(i => i.ingredient_id === selectedIngredientId)) {
      toast.error("This ingredient is already in the recipe");
      return;
    }

    const newIngredients = [
      ...formData.ingredients,
      {
        ingredient_id: selectedIngredientId,
        quantity: quantity
      }
    ];

    setFormData(prev => ({ ...prev, ingredients: newIngredients }));
    setSelectedIngredientId("");
    setQuantity("");
    toast.success("Ingredient added");
  };

  const handleRemoveIngredient = (ingredientId) => {
    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter(i => i.ingredient_id !== ingredientId)
    }));
  };

  const getIngredientName = (ingredientId) => {
    const ingredient = availableIngredients.find(i => i.id === ingredientId);
    return ingredient ? `${ingredient.name} (${ingredient.unit})` : "Unknown";
  };

  const getIngredientCost = (ingredientId, qty) => {
    const ingredient = availableIngredients.find(i => i.id === ingredientId);
    if (!ingredient) return "0.00";
    const cost = parseFloat(ingredient.unit_cost) * parseFloat(qty);
    return cost.toFixed(6);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Recipe name is required";
    }

    if (formData.ingredients.length === 0) {
      newErrors.ingredients = "Add at least one ingredient";
    }

    if (!formData.menu_price || isNaN(parseFloat(formData.menu_price)) || parseFloat(formData.menu_price) <= 0) {
      newErrors.menu_price = "Valid menu price is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAutoTranslate = async () => {
    if (!recipe?.id) {
      toast.error(t("recipeModal.autoTranslateRequiresSave"));
      return;
    }
    if (!formData.name && !formData.notes) {
      toast.error(t("recipeModal.autoTranslateSourceMissing"));
      return;
    }
    // Translate into every supported locale except EN (source = whatever the
    // chef typed; EN is treated as canonical and not stored separately).
    const targets = SUPPORTED_LANGUAGES
      .filter((l) => l.code !== "en")
      .map((l) => l.code);
    try {
      setTranslating(true);
      const res = await axios.post(`${API}/recipes/${recipe.id}/translate`, {
        targets,
        force: false,
      });
      setTranslations(res.data.translations || {});
      const translatedCount = (res.data.languages_translated || []).length;
      if (translatedCount > 0) {
        toast.success(
          t("toasts.translationSuccess", { count: translatedCount })
        );
      } else {
        toast.success(t("toasts.translationSkippedAll"));
      }
      setTranslationUsage({
        count: res.data.ai_translation_count_this_month,
        limit: res.data.monthly_limit,
        remaining: Math.max(
          0,
          res.data.monthly_limit - res.data.ai_translation_count_this_month
        ),
      });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402) {
        // Pro paywall — user hit free-tier (0 translations).
        toast.error(t("proPaywall.title"));
        // Reload usage so the UI swaps to the paywall variant.
        try {
          const usage = await axios.get(`${API}/usage/ai-translations`);
          setTranslationUsage(usage.data);
        } catch {
          /* ignore */
        }
      } else if (status === 429) {
        toast.error(t("toasts.translationLimitReached"));
      } else {
        toast.error(
          err?.response?.data?.detail || t("toasts.translationFailed")
        );
      }
    } finally {
      setTranslating(false);
    }
  };

  // BOH — translate the recipe `method` into a single Indic language via Sarvam.
  const handleTranslateBoh = async (langCode) => {
    if (!recipe?.id) {
      toast.error(t("recipeModal.autoTranslateRequiresSave"));
      return;
    }
    if (!formData.method || !formData.method.trim()) {
      toast.error(
        t("bohTranslate.missingMethod", {
          defaultValue: "Add the recipe method (prep instructions) first.",
        })
      );
      return;
    }
    try {
      setTranslatingBoh(true);
      const res = await axios.post(
        `${API}/recipes/${recipe.id}/translate-boh`,
        { targets: [langCode], force: false }
      );
      setBohTranslations(res.data.translated_methods || {});
      setBohActiveLang(langCode);
      const translated = (res.data.languages_translated || []).length;
      if (translated > 0) {
        toast.success(
          t("bohTranslate.success", {
            defaultValue: "BOH translation cached.",
          })
        );
      } else {
        toast.success(
          t("bohTranslate.cached", {
            defaultValue: "Showing cached translation.",
          })
        );
      }
      // Refresh shared usage badge from the FOH endpoint.
      try {
        const usage = await axios.get(`${API}/usage/ai-translations`);
        setTranslationUsage(usage.data);
      } catch {
        /* ignore */
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402) {
        toast.error(t("proPaywall.title"));
        try {
          const usage = await axios.get(`${API}/usage/ai-translations`);
          setTranslationUsage(usage.data);
        } catch {
          /* ignore */
        }
      } else if (status === 429) {
        toast.error(t("toasts.translationLimitReached"));
      } else {
        const d = err?.response?.data?.detail;
        toast.error(
          (typeof d === "string" ? d : d?.message) ||
            t("bohTranslate.failed", {
              defaultValue: "BOH translation failed.",
            })
        );
      }
    } finally {
      setTranslatingBoh(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fix the form errors");
      return;
    }

    setLoading(true);

    try {
      // 'Other' intercept — promote the typed-in name to a real menu section
      // before saving the recipe.
      const payload = { ...formData };
      if (
        payload.category === "Other" &&
        (payload.other_category_name || "").trim()
      ) {
        const customName = payload.other_category_name.trim();
        try {
          await handleCreateRecipeCategory(customName);
        } catch {
          setLoading(false);
          return;
        }
        payload.category = customName;
      }
      delete payload.other_category_name;

      // Coerce optional numerics: "" → null, "3" → number where appropriate
      payload.calories =
        payload.calories === "" || payload.calories === null
          ? null
          : Number(payload.calories);
      payload.spice_level =
        payload.spice_level === "" || payload.spice_level === null
          ? null
          : Number(payload.spice_level);
      payload.yield_amount =
        payload.yield_amount === "" ? null : payload.yield_amount;
      payload.yield_unit = payload.yield_unit || null;
      payload.portion_size = payload.portion_size || null;

      if (recipe) {
        await axios.put(`${API}/recipes/${recipe.id}`, payload);
        toast.success("Recipe updated successfully");
      } else {
        await axios.post(`${API}/recipes`, payload);
        toast.success("Recipe created successfully");
      }
      onClose(true);
    } catch (error) {
      console.error("Error saving recipe:", error);
      toast.error(error.response?.data?.detail || "Failed to save recipe");
    } finally {
      setLoading(false);
    }
  };

  const getProfitColor = () => {
    const profit = parseFloat(calculatedProfit);
    if (profit >= targetMargin) return "text-green-500";
    if (profit >= targetMargin * 0.85) return "text-amber-500";
    return "text-red-500";
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => onClose(false)}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="recipe-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl text-amber-500">
            {recipe ? "Edit Recipe" : "Create New Recipe"}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {recipe
              ? "Update recipe details and recalculate margins"
              : "Build a new recipe with cost and profit analysis"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Name and Category */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-300">
                Recipe Name *
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="e.g., Grilled Salmon, Caesar Salad"
                className={`bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 ${
                  errors.name ? "border-red-500" : ""
                }`}
                data-testid="recipe-name-input"
              />
              {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="category" className="text-slate-300">
                Category *
              </Label>
              <CreatableCombobox
                value={formData.category}
                options={categoryOptions}
                onChange={(val) => handleInputChange("category", val)}
                onCreate={handleCreateRecipeCategory}
                placeholder={t("combobox.searchPlaceholder")}
                searchPlaceholder={t("combobox.recipeSectionSearch")}
                emptyText={t("combobox.recipeSectionEmpty")}
                createLabel={t("combobox.recipeSectionCreateLabel")}
                existingHeading={t("combobox.existingHeading")}
                creatingLabel={t("combobox.creatingLabel")}
                testIdPrefix="recipe-category-combobox"
              />
              {formData.category === "Other" && (
                <div className="mt-2 space-y-1" data-testid="recipe-other-intercept">
                  <Label htmlFor="recipe-other-name" className="text-xs text-amber-400">
                    {t("recipeModal.otherCategoryHint")}
                  </Label>
                  <Input
                    id="recipe-other-name"
                    value={formData.other_category_name || ""}
                    onChange={(e) =>
                      handleInputChange("other_category_name", e.target.value)
                    }
                    placeholder={t("recipeModal.otherCategoryPlaceholder")}
                    className="bg-slate-800 border-amber-500/40 text-white placeholder:text-slate-500"
                    data-testid="recipe-other-input"
                  />
                  <p className="text-xs text-slate-500">
                    {t("recipeModal.otherCategoryFootnote")}
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="department" className="text-slate-300">
                Department *
              </Label>
              <Select value={formData.department} onValueChange={(value) => handleInputChange("department", value)}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="department-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {DEPARTMENTS.map((dept) => (
                    <SelectItem key={dept.value} value={dept.value} className="text-white">
                      {dept.emoji} {dept.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Ingredients Section */}
          <div className="space-y-4">
            <Label className="text-slate-300">Ingredients *</Label>
            
            {/* Add Ingredient */}
            <div className="flex gap-2">
              <Popover open={searchOpen} onOpenChange={setSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={searchOpen}
                    className="flex-1 justify-between bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
                  >
                    {selectedIngredientId
                      ? getIngredientName(selectedIngredientId)
                      : t("recipeModal.selectIngredientPlaceholder")}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0 bg-slate-800 border-slate-700">
                  <Command className="bg-slate-800">
                    <CommandInput placeholder={t("recipeModal.searchIngredientsPlaceholder")} className="text-white" />
                    <CommandEmpty className="text-slate-400 py-6 text-center">{t("recipeModal.noIngredientFound")}</CommandEmpty>
                    <CommandGroup className="max-h-64 overflow-y-auto">
                      {availableIngredients.map((ingredient) => (
                        <CommandItem
                          key={ingredient.id}
                          value={ingredient.name}
                          onSelect={() => {
                            setSelectedIngredientId(ingredient.id);
                            setSearchOpen(false);
                          }}
                          className="text-white hover:bg-slate-700 cursor-pointer"
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedIngredientId === ingredient.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <div className="flex-1">
                            <div>{ingredient.name}</div>
                            <div className="text-xs text-slate-400">
                              {formatCurrency(ingredient.unit_cost, currency, 6)}/{ingredient.unit}
                            </div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>

              <Input
                type="number"
                step="0.001"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder={t("recipeModal.qtyPlaceholder")}
                className="w-32 bg-slate-800 border-slate-700 text-white"
              />

              <Button
                onClick={handleAddIngredient}
                title={t("recipeModal.addButtonTooltip")}
                aria-label={t("recipeModal.addButtonTooltip")}
                className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold px-4 gap-1.5"
                data-testid="add-ingredient-button"
              >
                <Plus className="h-4 w-4" />
                {t("recipeModal.addButton")}
              </Button>
            </div>

            {errors.ingredients && <p className="text-red-500 text-sm">{errors.ingredients}</p>}

            {/* Ingredients List */}
            {formData.ingredients.length > 0 && (
              <div className="bg-slate-800/50 rounded-lg border border-slate-700 divide-y divide-slate-700">
                {formData.ingredients.map((recipeIng) => {
                  const ingredient = availableIngredients.find(i => i.id === recipeIng.ingredient_id);
                  return (
                    <div key={recipeIng.ingredient_id} className="p-3 flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-white font-medium">{ingredient?.name || "Unknown"}</div>
                        <div className="text-sm text-slate-400">
                          {recipeIng.quantity} {ingredient?.unit} × {formatCurrency(ingredient?.unit_cost || "0", currency, 6)}/{ingredient?.unit}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="text-amber-500 font-mono font-semibold">
                            {formatCurrency(getIngredientCost(recipeIng.ingredient_id, recipeIng.quantity), currency)}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveIngredient(recipeIng.ingredient_id)}
                          className="hover:bg-red-500/10 text-slate-400 hover:text-red-500"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Menu Price */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="menu_price" className="text-slate-300">
                Menu Price ({currency}) *
              </Label>
              <Input
                id="menu_price"
                type="number"
                step="0.01"
                value={formData.menu_price}
                onChange={(e) => handleInputChange("menu_price", e.target.value)}
                placeholder="25.00"
                className={`bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 font-mono ${
                  errors.menu_price ? "border-red-500" : ""
                }`}
                data-testid="menu-price-input"
              />
              {errors.menu_price && <p className="text-red-500 text-sm">{errors.menu_price}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="serving_size" className="text-slate-300">
                Serving Size
              </Label>
              <Input
                id="serving_size"
                value={formData.serving_size}
                onChange={(e) => handleInputChange("serving_size", e.target.value)}
                placeholder="1"
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
          </div>

          {/* Calculated Metrics */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <div className="text-sm text-slate-400 mb-1">Total Cost</div>
              <div className="text-2xl font-bold text-white font-mono" data-testid="calculated-cost">
                {formatCurrency(calculatedCost, currency)}
              </div>
            </div>

            <div className={cn(
              "border rounded-lg p-4",
              parseFloat(calculatedProfit) >= targetMargin 
                ? "bg-green-500/10 border-green-500/30" 
                : "bg-amber-500/10 border-amber-500/30"
            )}>
              <div className="text-sm text-slate-400 mb-1 flex items-center">
                PlateProfit %
                <HelpTip title="PlateProfit %" testId="help-plate-profit">
                  The margin this dish keeps after ingredient cost — calculated as
                  <span className="block font-mono text-amber-400 my-1.5">
                    (menu price − total cost) ÷ menu price × 100
                  </span>
                  Compare this against your <strong>Target Margin</strong> in the
                  bottom row to see if it's an Engine (≥ target, green) or
                  Artistic dish (below target, amber).
                </HelpTip>
              </div>
              <div className={`text-2xl font-bold font-mono ${getProfitColor()}`} data-testid="calculated-profit">
                {calculatedProfit}%
              </div>
              <div className="text-xs text-slate-500 mt-1 flex items-center">
                Target: {targetMargin}%
                <HelpTip
                  title="Target Margin"
                  side="top"
                  align="end"
                  testId="help-target-margin-modal"
                >
                  The minimum PlateProfit % every active dish should clear to be
                  considered profitable in your kitchen. Set this once in{" "}
                  <strong>Settings → Target Margin</strong>; the whole app uses
                  it as the green/amber threshold.
                </HelpTip>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-slate-300">
              {t("recipeModal.notes")}
            </Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange("notes", e.target.value)}
              placeholder={t("recipeModal.notesPlaceholder")}
              className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 min-h-[80px]"
              data-testid="notes-input"
            />
          </div>

          {/* ============== MENU METADATA (PUBLIC) ============== */}
          <div className="border border-slate-800 rounded-lg p-4 bg-slate-900/40 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm uppercase tracking-[0.18em] text-amber-500 font-semibold">
                {t("recipeModal.menuMetadata")}
              </h3>
              <span className="text-xs text-slate-500">
                {t("recipeModal.menuMetadataHint")}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="calories" className="text-slate-300 text-sm">
                  {t("recipeModal.calories")}
                </Label>
                <Input
                  id="calories"
                  type="number"
                  inputMode="numeric"
                  min="0"
                  value={formData.calories}
                  onChange={(e) => handleInputChange("calories", e.target.value)}
                  placeholder={t("recipeModal.caloriesPlaceholder")}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 font-mono"
                  data-testid="recipe-calories-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="spice_level" className="text-slate-300 text-sm">
                  {t("recipeModal.spiceLevel")}
                </Label>
                <div
                  className="flex items-center gap-1"
                  data-testid="recipe-spice-level-picker"
                >
                  {[0, 1, 2, 3].map((lvl) => {
                    const active = String(formData.spice_level) === String(lvl);
                    return (
                      <button
                        key={lvl}
                        type="button"
                        onClick={() =>
                          handleInputChange(
                            "spice_level",
                            active ? "" : lvl
                          )
                        }
                        data-testid={`recipe-spice-${lvl}`}
                        className={cn(
                          "flex-1 h-9 rounded-md border text-xs font-semibold transition-all",
                          active
                            ? "bg-amber-500/20 border-amber-500 text-amber-400"
                            : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600"
                        )}
                        title={
                          [
                            t("spiceLevels.titleNone"),
                            t("spiceLevels.mild"),
                            t("spiceLevels.medium"),
                            t("spiceLevels.hot"),
                          ][lvl]
                        }
                      >
                        {lvl === 0 ? (
                          t("spiceLevels.none")
                        ) : (
                          <span className="inline-flex items-center justify-center gap-0.5">
                            {Array.from({ length: lvl }).map((_, i) => (
                              <Flame key={i} className="h-3.5 w-3.5" />
                            ))}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">{t("recipeModal.dietaryTags")}</Label>
              <div
                className="flex flex-wrap gap-2"
                data-testid="recipe-dietary-tags-picker"
              >
                {DIETARY_TAGS.map((tag) => {
                  const Icon = tag.icon;
                  const active = formData.dietary_tags.includes(tag.value);
                  const translatedLabel =
                    tag.value === "Gluten-Free"
                      ? t("dietaryTags.GFShort")
                      : t(`dietaryTags.${tag.value}`, { defaultValue: tag.label });
                  return (
                    <button
                      key={tag.value}
                      type="button"
                      onClick={() => {
                        const next = active
                          ? formData.dietary_tags.filter((t2) => t2 !== tag.value)
                          : [...formData.dietary_tags, tag.value];
                        handleInputChange("dietary_tags", next);
                      }}
                      data-testid={`recipe-dietary-${tag.value}`}
                      className={cn(
                        "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition-colors",
                        active
                          ? "bg-emerald-500/15 border-emerald-500/60 text-emerald-300"
                          : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600"
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {translatedLabel}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ============== YIELD & PORTIONS (PRIVATE / BOH) ============== */}
          <div className="border border-slate-800 rounded-lg p-4 bg-slate-900/40 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm uppercase tracking-[0.18em] text-emerald-500 font-semibold">
                {t("recipeModal.yieldAndPortions")}
              </h3>
              <span className="text-xs text-slate-500">
                {t("recipeModal.yieldAndPortionsHint")}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label htmlFor="yield_amount" className="text-slate-300 text-sm">
                  {t("recipeModal.yieldAmount")}
                </Label>
                <Input
                  id="yield_amount"
                  type="text"
                  inputMode="decimal"
                  value={formData.yield_amount}
                  onChange={(e) => handleInputChange("yield_amount", e.target.value)}
                  placeholder={t("recipeModal.yieldAmountPlaceholder")}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 font-mono"
                  data-testid="recipe-yield-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="yield_unit" className="text-slate-300 text-sm">
                  {t("recipeModal.yieldUnit")}
                </Label>
                <Select
                  value={formData.yield_unit || ""}
                  onValueChange={(val) =>
                    handleInputChange("yield_unit", val === "__none__" ? "" : val)
                  }
                >
                  <SelectTrigger
                    id="yield_unit"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="recipe-yield-unit-select"
                  >
                    <SelectValue placeholder={t("recipeModal.yieldUnitPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700 text-white">
                    <SelectItem value="__none__" className="text-slate-400">
                      {t("recipeModal.yieldUnitNone")}
                    </SelectItem>
                    {YIELD_UNIT_GROUPS.map((group) => (
                      <SelectGroup key={group.label}>
                        <SelectLabel className="text-amber-400 text-[0.7rem] uppercase tracking-[0.18em]">
                          {t(`yieldGroups.${group.label}`, { defaultValue: group.label })}
                        </SelectLabel>
                        {group.options.map((opt) => (
                          <SelectItem
                            key={opt}
                            value={opt}
                            data-testid={`yield-unit-option-${opt.replace(/\s+/g, "-")}`}
                            className="text-white focus:bg-slate-800 focus:text-amber-400"
                          >
                            {opt}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="portion_size" className="text-slate-300 text-sm">
                  {t("recipeModal.portionSize")}
                </Label>
                <Input
                  id="portion_size"
                  value={formData.portion_size}
                  onChange={(e) => handleInputChange("portion_size", e.target.value)}
                  placeholder={t("recipeModal.portionSizePlaceholder")}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="recipe-portion-size-input"
                />
              </div>
            </div>
            <p
              className="text-xs text-slate-500 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: t("recipeModal.yieldHelp") }}
            />

            {/* ---- Live "Total Batch Creates" derived display ---- */}
            {(() => {
              const yieldNum = parseFloat(formData.yield_amount);
              const portionMatch = String(formData.portion_size || "").match(
                /-?\d*\.?\d+/
              );
              const portionNum = portionMatch ? parseFloat(portionMatch[0]) : NaN;

              if (!yieldNum || yieldNum <= 0) return null;

              let totalPortions = null;
              let basis = null;
              if (portionNum && portionNum > 0) {
                totalPortions = Math.floor(yieldNum / portionNum);
                basis = `${yieldNum}${formData.yield_unit ? " " + formData.yield_unit : ""} ÷ ${portionNum}${formData.yield_unit ? " " + formData.yield_unit : ""}`;
              } else {
                totalPortions = Math.floor(yieldNum);
                basis = t("recipeModal.basedOnYield");
              }

              if (totalPortions <= 0) return null;

              return (
                <div
                  className="mt-1 flex items-center justify-between rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2"
                  data-testid="recipe-total-portions-display"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-[0.65rem] uppercase tracking-[0.18em] text-emerald-400 font-semibold">
                      {t("recipeModal.totalBatchCreates")}
                    </span>
                    <span className="text-xs text-slate-500">{basis}</span>
                  </div>
                  <div
                    className="font-mono text-emerald-300 text-sm font-semibold"
                    data-testid="recipe-total-portions-value"
                  >
                    {totalPortions} {totalPortions === 1 ? t("recipeModal.portion") : t("recipeModal.portions")}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* ============== TOURIST MENU · AUTO-TRANSLATE (PRO PAYWALL) ============== */}
          {recipe?.id && (() => {
            const isPro = translationUsage?.is_pro === true;
            const paywalled = translationUsage && !isPro;
            return (
              <div
                className={cn(
                  "rounded-lg p-4 space-y-3 border",
                  paywalled
                    ? "border-amber-400/40 bg-gradient-to-br from-amber-500/15 via-slate-900 to-slate-900"
                    : "border-amber-500/30 bg-amber-500/5"
                )}
                data-testid="recipe-auto-translate-section"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-sm uppercase tracking-[0.18em] text-amber-500 font-semibold flex items-center gap-2">
                    <Languages className="h-4 w-4" />
                    {t("recipeModal.autoTranslateSection")}
                  </h3>
                  {paywalled ? (
                    <span className="text-[0.6rem] uppercase tracking-[0.22em] bg-amber-500/20 border border-amber-500/50 text-amber-300 px-2 py-0.5 rounded-full font-semibold">
                      {t("proPaywall.badge")}
                    </span>
                  ) : (
                    <span className="text-xs text-amber-500/70">
                      {t("recipeModal.autoTranslateHint")}
                    </span>
                  )}
                </div>

                {paywalled ? (
                  <div data-testid="recipe-auto-translate-paywall" className="space-y-3">
                    <div>
                      <h4 className="text-lg font-bold text-white mb-1.5">
                        {t("proPaywall.title")}
                      </h4>
                      <p className="text-sm text-slate-400 leading-relaxed">
                        {t("proPaywall.body")}
                      </p>
                    </div>
                    <div className="flex items-center justify-between gap-3 pt-1">
                      <div className="text-[0.7rem] text-slate-500">
                        {t("proPaywall.included", { count: 500 })}
                      </div>
                      <Button
                        type="button"
                        onClick={() => setShowUpgradePreview(true)}
                        className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold gap-2"
                        data-testid="recipe-auto-translate-upgrade"
                      >
                        <Sparkles className="h-4 w-4" />
                        {t("proPaywall.cta")}
                        <span className="text-[0.7rem] font-mono bg-slate-950/20 px-1.5 py-0.5 rounded">
                          {t("proPaywall.price")}
                        </span>
                      </Button>
                    </div>
                    <div className="text-[0.65rem] text-slate-500 italic pt-2 border-t border-amber-500/20">
                      {t("proPaywall.stripeBillingNote")}
                    </div>
                  </div>
                ) : (
                  <>
                    <Button
                      type="button"
                      onClick={handleAutoTranslate}
                      disabled={
                        translating ||
                        !(formData.name || formData.notes) ||
                        (translationUsage &&
                          translationUsage.remaining !== undefined &&
                          translationUsage.remaining <= 0)
                      }
                      className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold disabled:opacity-60"
                      data-testid="recipe-auto-translate-button"
                    >
                      <Sparkles className="h-4 w-4" />
                      {translating
                        ? t("recipeModal.autoTranslateRunning")
                        : t("recipeModal.autoTranslateButton")}
                    </Button>

                    <div className="flex flex-wrap gap-1.5" data-testid="recipe-translation-chips">
                      {(() => {
                        const cachedCodes = SUPPORTED_LANGUAGES
                          .filter((l) => l.code !== "en" && translations[l.code])
                          .map((l) => l);
                        if (cachedCodes.length === 0) {
                          return (
                            <span className="text-xs text-slate-500 italic">
                              {t("recipeModal.autoTranslateNoneYet")}
                            </span>
                          );
                        }
                        return (
                          <>
                            <span className="text-[0.65rem] uppercase tracking-[0.18em] text-slate-500 self-center mr-1">
                              {t("recipeModal.autoTranslateCachedFor")}
                            </span>
                            {cachedCodes.map((l) => (
                              <span
                                key={l.code}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 text-xs"
                                data-testid={`recipe-translation-chip-${l.code}`}
                                title={l.label}
                              >
                                <span className="leading-none">{l.flag}</span>
                                <span className="uppercase tracking-wider text-[0.65rem]">
                                  {l.code}
                                </span>
                              </span>
                            ))}
                          </>
                        );
                      })()}
                    </div>

                    {translationUsage && (
                      <div className="flex items-center justify-between text-[0.7rem] text-slate-500 pt-2 border-t border-amber-500/20">
                        <span data-testid="recipe-translation-usage">
                          {t("recipeModal.autoTranslateUsage", {
                            used: translationUsage.count,
                            limit: translationUsage.limit,
                          })}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()}

          {/* ============== BOH (KITCHEN) TRANSLATIONS — Sarvam Indic ============== */}
          {recipe?.id && translationUsage?.is_pro && (
            <div
              className="rounded-lg border border-emerald-500/30 bg-gradient-to-br from-emerald-950/40 to-slate-950/40 p-4 space-y-3"
              data-testid="recipe-boh-translate-section"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                    <ChefHat className="h-4 w-4 text-emerald-400" />
                    {t("bohTranslate.title", {
                      defaultValue: "Kitchen Translations · BOH",
                    })}
                  </h3>
                  <p className="text-[0.7rem] text-slate-400 mt-0.5">
                    {t("bohTranslate.subtitle", {
                      defaultValue:
                        "Translate your prep method for line cooks who read Indic languages.",
                    })}
                  </p>
                </div>
                <span className="text-[0.6rem] uppercase tracking-[0.18em] text-emerald-400 font-mono px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/30">
                  PRO
                </span>
              </div>

              {/* Language pills */}
              <div
                className="flex flex-wrap gap-1.5"
                data-testid="recipe-boh-translate-pills"
              >
                {BOH_LANGUAGES.map((lang) => {
                  const cached = bohTranslations?.[lang.code];
                  const active = bohActiveLang === lang.code;
                  return (
                    <button
                      key={lang.code}
                      type="button"
                      disabled={translatingBoh}
                      onClick={() =>
                        cached
                          ? setBohActiveLang(lang.code)
                          : handleTranslateBoh(lang.code)
                      }
                      className={cn(
                        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border transition-all",
                        active
                          ? "bg-emerald-500 text-slate-950 border-emerald-400 font-semibold shadow-lg shadow-emerald-500/30"
                          : cached
                          ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25"
                          : "bg-slate-800 text-slate-300 border-slate-700 hover:border-emerald-500/50",
                        translatingBoh && "opacity-50 cursor-wait"
                      )}
                      data-testid={`recipe-boh-translate-pill-${lang.code}`}
                    >
                      <span className="text-sm leading-none">{lang.label}</span>
                      {cached && <Check className="h-3 w-3" />}
                    </button>
                  );
                })}
              </div>

              {/* Active translation preview */}
              {bohTranslations?.[bohActiveLang]?.method ? (
                <div
                  className="rounded-md border border-emerald-500/30 bg-slate-950/60 p-3 max-h-48 overflow-y-auto"
                  data-testid="recipe-boh-translate-preview"
                >
                  <div className="text-[0.6rem] uppercase tracking-[0.25em] text-emerald-400 font-semibold mb-1.5 flex items-center justify-between">
                    <span>
                      {BOH_LANGUAGES.find((l) => l.code === bohActiveLang)?.english}{" "}
                      · {bohActiveLang}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleTranslateBoh(bohActiveLang)}
                      disabled={translatingBoh}
                      className="text-[0.65rem] text-emerald-300 hover:text-emerald-200 underline disabled:opacity-50"
                      data-testid="recipe-boh-translate-refresh"
                    >
                      {translatingBoh
                        ? t("bohTranslate.translating", {
                            defaultValue: "Translating…",
                          })
                        : t("bohTranslate.retranslate", {
                            defaultValue: "Re-translate",
                          })}
                    </button>
                  </div>
                  <p
                    className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed"
                    dir="ltr"
                  >
                    {bohTranslations[bohActiveLang].method}
                  </p>
                </div>
              ) : (
                <p className="text-[0.7rem] text-slate-500 italic text-center py-3">
                  {translatingBoh
                    ? t("bohTranslate.translating", {
                        defaultValue: "Translating…",
                      })
                    : t("bohTranslate.empty", {
                        defaultValue:
                          "Tap a language above to translate this method.",
                      })}
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onClose(false)}
            className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
            disabled={loading}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"
            disabled={loading}
            data-testid="save-recipe-button"
          >
            {loading
              ? t("common.saving")
              : recipe
              ? t("recipeModal.updateRecipe")
              : t("recipeModal.createRecipe")}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* ============== INLINE TOURIST MENU UPGRADE PREVIEW ============== */}
      <Dialog open={showUpgradePreview} onOpenChange={setShowUpgradePreview}>
        <DialogContent
          className="max-w-lg bg-slate-900 border-amber-500/40"
          data-testid="tourist-upgrade-preview-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2 text-white">
              <Sparkles className="h-5 w-5 text-amber-400" />
              {t("touristPreview.title")}
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              {t("touristPreview.subtitle")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Language pills */}
            <div className="flex flex-wrap gap-2" data-testid="tourist-upgrade-locale-picker">
              {Object.keys(UPGRADE_PREVIEW_DEMOS).map((code) => {
                const lang = SUPPORTED_LANGUAGES.find((l) => l.code === code);
                const active = previewLocale === code;
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setPreviewLocale(code)}
                    className={cn(
                      "px-3 py-1.5 rounded-full text-xs font-semibold border transition-all",
                      active
                        ? "bg-amber-500 text-slate-950 border-amber-400 shadow-lg shadow-amber-500/30"
                        : "bg-slate-800 text-slate-300 border-slate-700 hover:border-amber-500/50"
                    )}
                    data-testid={`tourist-upgrade-locale-${code}`}
                  >
                    {lang?.flag} {lang?.label || code}
                  </button>
                );
              })}
            </div>

            {/* Before / After split — fade+slide-in on locale change */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="rounded-md border border-slate-800 bg-slate-950/60 p-3">
                <div className="text-[0.6rem] uppercase tracking-[0.25em] text-slate-500 font-semibold mb-1.5">
                  {t("touristPreview.originalLabel")}
                </div>
                <div className="text-sm font-semibold text-slate-200 mb-1">
                  {UPGRADE_PREVIEW_ORIGINAL.name}
                </div>
                <div className="text-xs text-slate-400 leading-relaxed">
                  {UPGRADE_PREVIEW_ORIGINAL.notes}
                </div>
              </div>

              <div
                key={previewLocale}
                className="rounded-md border border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-slate-900 p-3 animate-in fade-in slide-in-from-right-4 duration-500"
                data-testid="tourist-upgrade-translated-pane"
              >
                <div className="text-[0.6rem] uppercase tracking-[0.25em] text-amber-400 font-semibold mb-1.5">
                  {t("touristPreview.translatedLabel")}
                </div>
                <div
                  className="text-sm font-semibold text-white mb-1"
                  dir={previewLocale === "ar" ? "rtl" : "ltr"}
                >
                  {UPGRADE_PREVIEW_DEMOS[previewLocale]?.name}
                </div>
                <div
                  className="text-xs text-slate-300 leading-relaxed"
                  dir={previewLocale === "ar" ? "rtl" : "ltr"}
                >
                  {UPGRADE_PREVIEW_DEMOS[previewLocale]?.notes}
                </div>
              </div>
            </div>

            <p className="text-[0.7rem] text-slate-500 italic text-center border-t border-slate-800 pt-3">
              {t("touristPreview.disclaimer")}
            </p>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowUpgradePreview(false)}
              className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700"
              data-testid="tourist-upgrade-preview-cancel"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              onClick={() => {
                try {
                  window.localStorage.setItem(
                    "pp_upgrade_source",
                    "tourist-menu-preview"
                  );
                } catch {
                  /* ignore */
                }
                window.location.href = "/settings?tab=billing";
              }}
              className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold gap-2"
              data-testid="tourist-upgrade-preview-confirm"
            >
              <Sparkles className="h-4 w-4" />
              {t("touristPreview.confirmUpgrade")}
              <span className="text-[0.7rem] font-mono bg-slate-950/20 px-1.5 py-0.5 rounded">
                {t("proPaywall.price")}
              </span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default RecipeModal;
